import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Download, Mail, Send, Users, Inbox } from "lucide-react";

export default function AdminNewsletter() {
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/newsletter/subscribers");
      setSubs(data.subscribers || []);
      setStats({ total: data.total || 0, active: data.active || 0 });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const exportCsv = () => {
    if (!subs.length) { toast.error("No subscribers to export"); return; }
    const rows = [["email", "source", "status", "created_at"]];
    subs.forEach((s) => rows.push([s.email, s.source || "", s.status || "", s.created_at || ""]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pawlinx-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and message are required"); return; }
    if (!window.confirm(`Send this newsletter to ${stats.active} subscriber(s)?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", { subject, body });
      toast.success(data.message || "Sent");
      setSubject(""); setBody("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-5xl" data-testid="admin-newsletter">
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Audience</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Newsletter</h1>
          <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mt-2">Subscribers from the site signup popup, plus a tool to email them.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 font-manrope text-sm transition-colors" data-testid="newsletter-export">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={load} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 font-manrope text-sm transition-colors" data-testid="newsletter-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-8 max-w-md">
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-5">
          <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-xs mb-2"><Users className="w-4 h-4" /> Total</div>
          <p className="font-outfit font-bold text-3xl">{stats.total}</p>
        </div>
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-5">
          <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-xs mb-2"><Mail className="w-4 h-4" /> Active</div>
          <p className="font-outfit font-bold text-3xl text-emerald-700 dark:text-emerald-400">{stats.active}</p>
        </div>
      </div>

      {/* Compose */}
      <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-5 sm:p-6 mb-8">
        <h2 className="font-outfit font-bold text-lg mb-1 flex items-center gap-2"><Send className="w-4 h-4 text-paw-accent" /> Compose a newsletter</h2>
        <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mb-5">Sends a plain message to every active subscriber. Until SMTP credentials are configured on the server, emails are written to the backend logs instead of delivered.</p>
        <div className="space-y-3">
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject line"
            className="w-full h-11 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 font-manrope text-sm focus:outline-none focus:border-paw-accent text-paw-text dark:text-white placeholder:text-paw-text-muted dark:placeholder:text-white/30"
            data-testid="newsletter-subject"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your message…"
            rows={6}
            className="w-full px-3 py-2.5 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 font-manrope text-sm focus:outline-none focus:border-paw-accent text-paw-text dark:text-white placeholder:text-paw-text-muted dark:placeholder:text-white/30 resize-y"
            data-testid="newsletter-body"
          />
          <div className="flex justify-end">
            <button onClick={send} disabled={sending} className="flex items-center gap-2 px-5 h-10 rounded-lg bg-paw-accent hover:bg-paw-accent-hover disabled:opacity-60 text-white font-manrope text-sm font-semibold transition-colors" data-testid="newsletter-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to {stats.active}
            </button>
          </div>
        </div>
      </div>

      {/* Subscribers */}
      <h2 className="font-outfit font-bold text-lg mb-4">Subscribers</h2>
      {loading ? (
        <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : subs.length === 0 ? (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-12 text-center font-manrope text-paw-text-muted dark:text-white/40">
          <Inbox className="w-8 h-8 mx-auto mb-3 text-paw-text-muted dark:text-white/20" strokeWidth={1.5} />
          No subscribers yet. They'll appear here once visitors sign up.
        </div>
      ) : (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left font-manrope text-sm">
              <thead>
                <tr className="text-paw-text-muted dark:text-white/40 text-xs uppercase tracking-wider border-b border-paw-border dark:border-white/5">
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Source</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.email} className="border-b border-paw-border dark:border-white/5 last:border-0">
                    <td className="px-5 py-3 text-paw-text dark:text-white/90">{s.email}</td>
                    <td className="px-5 py-3 text-paw-text-muted dark:text-white/50">{s.source || "site"}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs ${s.status === "unsubscribed" ? "text-paw-text-muted dark:text-white/30" : "text-emerald-700 dark:text-emerald-400"}`}>{s.status || "subscribed"}</span>
                    </td>
                    <td className="px-5 py-3 text-paw-text-muted dark:text-white/40">{(s.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
